import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSuperAdminFacilitators, FacilitatorUsage as Facilitator } from '../../services/api';
import SuperAdminLayout from './SuperAdminLayout';

const DAY = 24 * 60 * 60 * 1000;
const relTime = (iso?: string | null): { label: string; days: number | null } => {
  if (!iso) return { label: 'Never', days: null };
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return { label: '—', days: null };
  const days = Math.floor((Date.now() - t) / DAY);
  if (days <= 0) return { label: 'Today', days: 0 };
  if (days === 1) return { label: 'Yesterday', days: 1 };
  if (days < 7) return { label: `${days}d ago`, days };
  if (days < 30) return { label: `${Math.floor(days / 7)}w ago`, days };
  return { label: new Date(iso).toLocaleDateString(), days };
};
const fmtMins = (m?: number | null): string => {
  if (!m) return '—';
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${Math.round(m % 60)}m` : `${Math.round(m)}m`;
};
const classLabel = (f: Facilitator): string =>
  f.classes.length === 0 ? '—' : f.classes.map((c) => `${c.grade}${c.section ? `-${c.section}` : ''}`).join(', ');

/** Facilitators lens on the super-admin surface (artifact styling). */
const SuperAdminFacilitators: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Facilitator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    if (!token) {
      navigate('/super-admin/login', { replace: true });
      return;
    }
    getSuperAdminFacilitators(token)
      .then(setRows)
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        if (/token|unauth/i.test(msg)) {
          localStorage.removeItem('superAdminToken');
          navigate('/super-admin/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = term
      ? rows.filter((f) => (f.name || '').toLowerCase().includes(term) || f.username.toLowerCase().includes(term) || f.school.toLowerCase().includes(term))
      : rows;
    return [...list].sort((a, b) => {
      const ax = a.last_active ? new Date(a.last_active).getTime() : -1;
      const bx = b.last_active ? new Date(b.last_active).getTime() : -1;
      return bx - ax;
    });
  }, [rows, q]);

  return (
    <SuperAdminLayout>
      <div className="content">
        <h1 className="pg">Facilitators</h1>
        <p className="pg-sub">Every facilitator across all schools. Click one for their activity, plans, and chats.</p>

        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, username, or school…"
          style={{
            width: '100%', marginBottom: 12, padding: '8px 12px', fontSize: 13,
            borderRadius: 10, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--ink)', outline: 'none',
          }}
        />

        {loading ? (
          <p className="hint">Loading…</p>
        ) : error ? (
          <p className="hint" style={{ color: 'var(--crit)' }}>{error}</p>
        ) : (
          <div className="tblwrap">
            <table>
              <thead>
                <tr>
                  <th>Facilitator</th>
                  <th>Classes</th>
                  <th>Last active</th>
                  <th className="num">Coach time</th>
                  <th className="num">Chats</th>
                  <th className="num">Topics</th>
                  <th className="num">HW / Exams</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--faint)', padding: '30px' }}>No facilitators found.</td></tr>
                ) : (
                  filtered.map((f) => {
                    const rt = relTime(f.last_active);
                    const tone = rt.days == null ? 'neutral' : rt.days < 7 ? 'good' : rt.days < 30 ? 'warn' : 'crit';
                    return (
                      <tr key={f.id} className="clk" onClick={() => navigate(`/super-admin/facilitators/${f.id}`)}>
                        <td className="who">{f.name || f.username}<small>@{f.username} · {f.school}</small></td>
                        <td>{classLabel(f)}</td>
                        <td><span className={`pill ${tone}`}>{rt.label}</span></td>
                        <td className="num">{fmtMins(f.coach_minutes)}</td>
                        <td className="num">{f.coach_chats}</td>
                        <td className="num">{f.topics_ticked}</td>
                        <td className="num">{f.homeworks_set} / {f.exams_set}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminFacilitators;
